import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { UsersThree, Hospital, Stethoscope, GearSix, CaretDown, SignOut, List, X, ArrowLeft } from '@phosphor-icons/react';
import { useAuth } from '../lib/auth';

const navItems = [
  { path: '/admin/users', label: 'Users', icon: UsersThree },
  { path: '/admin/clinics', label: 'Clinics', icon: Hospital },
  { path: '/admin/clinicians', label: 'Clinicians', icon: Stethoscope },
  { path: '/admin/settings', label: 'Global Settings', icon: GearSix },
];

export default function AdminLayout() {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);

  return (
    <div className="flex flex-col min-h-screen bg-[#F8F8F8]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-[60px] bg-white border-b border-[#E6E6E6] flex items-center justify-between px-4 md:px-6 z-20">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden text-gray-600 hover:text-gray-900 p-1"
          >
            {sidebarOpen ? <X size={22} /> : <List size={22} />}
          </button>
          <div className="cursor-pointer" onClick={() => navigate('/admin/users')}>
            <img src="/cscore-logo.png" alt="CScore" style={{ height: '28px', filter: 'brightness(0) saturate(100%) invert(13%) sepia(90%) saturate(5500%) hue-rotate(270deg) brightness(80%) contrast(110%)' }} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-[13px] font-bold text-text leading-tight">
              {profile?.first_name?.toUpperCase()} {profile?.last_name?.toUpperCase()}
            </p>
            <p className="text-[11px] text-gray-500 leading-tight">{profile?.email}</p>
          </div>
          <CaretDown size={14} className="text-gray-400 hidden sm:block" />
          <button
            onClick={() => setLogoutModalOpen(true)}
            className="ml-1 sm:ml-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Sign out"
          >
            <SignOut size={18} />
          </button>
        </div>
      </header>

      <div className="flex flex-1 pt-[60px]">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/30 z-10 md:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={`fixed left-0 top-[60px] bottom-0 w-[200px] bg-white border-r border-[#E6E6E6] flex flex-col py-4 px-3 z-10 transition-transform md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <nav className="flex-1">
            {navItems.map(({ path, label, icon: Icon }) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 text-[13px] font-medium transition-colors ${
                    isActive
                      ? 'text-primary bg-[#F5E9FD]'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`
                }
              >
                <Icon size={20} weight="regular" />
                {label}
              </NavLink>
            ))}
          </nav>
          <button
            type="button"
            onClick={() => { navigate('/'); setSidebarOpen(false); }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg mb-2 text-[13px] font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors w-full"
          >
            <ArrowLeft size={20} weight="regular" />
            Portal
          </button>
          <p className="text-[11px] text-gray-400 px-3">v1.0.0</p>
        </aside>

        {/* Content */}
        <main className="flex-1 md:ml-[200px] p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {/* Logout confirmation modal */}
      {logoutModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={() => setLogoutModalOpen(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-text mb-2">Sign out</h3>
            <p className="text-sm text-gray-500 mb-6">Are you sure you want to sign out?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setLogoutModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={signOut}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
